import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  FindOrdersQuery,
  MemberSituationDTO,
  OrderDto,
  UpdateOrderDto,
} from '@services/dtos'
import { PaginatedDto } from '@services/extendedType'
import { DocumentService } from '@services/StorageService'
import { OrderService } from '@services/OrderService'
import moment from 'moment'
import { OrderStatusEnum } from '../../common/interfaces'
import { Document, MemberDto, Report } from '../../services/api'
import type { RootState } from '../store'
import {
  createPatientTransport,
  updatePatientTransport,
} from '../thunks/ambulanceThunk'
import { updatePatient } from '../thunks/authThunk'
import { doGetOrderTransports } from './ambulanceReducer'
import { doCreateComment, doDeleteComment } from './CommentReducer'

interface OrderFilter {
  status?: OrderStatusEnum
  search?: string
  page?: number
  limit?: number
  total?: number
  startDate?: number
  endDate?: number
  siteId?: number
  patientIds?: number[]
  hasRadiologyStudy?: boolean
}

export interface OrderState {
  orders: OrderDto[]
  filters: OrderFilter
  selectedOrder: OrderDto | null
  orderHistory: PaginatedDto<OrderDto>
  selectedMemberSituation: {
    member: MemberDto
    situation: MemberSituationDTO
  } | null
  isFloatingMemberSituationExpanded: boolean
}

const m = moment()
const initialState: OrderState = {
  orders: [],
  filters: {
    status: OrderStatusEnum.pendingRegistration,
    limit: 50,
    page: 0,
    total: 0,
    startDate: m.startOf('day').toDate().getTime(),
    endDate: m.endOf('day').toDate().getTime(),
  },
  selectedOrder: null,
  orderHistory: {
    datas: [],
    totalCount: 0,
  },
  selectedMemberSituation: null,
  isFloatingMemberSituationExpanded: false,
}

export const selectPatientOrder = (state: RootState) =>
  state.order.selectedOrder?.patient

export const doFindOrders = createAsyncThunk<PaginatedDto<OrderDto>, void>(
  'order/findOrders',
  async (_, { getState }) => {
    const { filters } = (getState() as { order: OrderState }).order
    const query: FindOrdersQuery = {
      page: filters.page,
      limit: filters.limit,
    }
    if (filters.search) {
      query.search = filters.search
      return OrderService.get(query)
    }
    if (filters.siteId) {
      query.siteId = filters.siteId
    }
    if (filters.patientIds && filters.patientIds.length > 0) {
      query.patientIds = filters.patientIds
      query.status = filters.status
      return OrderService.get(query)
    }

    if (filters.hasRadiologyStudy) {
      query.hasRadiologyStudy = true
    }

    if (filters.status) {
      query.status = filters.status
    }

    query.startDate = moment(filters.startDate).toISOString()
    query.endDate = moment(filters.endDate).toISOString()


    return OrderService.get(query)
  },
)

export const doGetOrder = createAsyncThunk<OrderDto, number>(
  'order/getOrder',
  async (id) => {
    return OrderService.getById(id)
  },
)

export const doUpdateMedicalOrdersEquipment = createAsyncThunk<
  OrderDto,
  {
    orderId: number
    medicalOrderIds: number[]
    medicalEquipmentId: number | null
  }
>(
  'order/updateMedicalOrdersEquipment',
  async ({ orderId, medicalOrderIds, medicalEquipmentId }) => {
    return OrderService.updateMedicalOrdersEquipment(
      orderId,
      medicalOrderIds,
      medicalEquipmentId,
    )
  },
)

export const doUpdateMedicalOrderAttendingDoctor = createAsyncThunk<
  OrderDto,
  {
    orderId: number
    medicalOrderIds: number[]
    attendingDoctorId: number | null
  }
>(
  'order/updateMedicalOrderAttendingDoctor',
  async ({ orderId, medicalOrderIds, attendingDoctorId }) => {
    return OrderService.updateMedicalOrdersAttendingDoctor(
      orderId,
      medicalOrderIds,
      attendingDoctorId,
    )
  },
)

export const doAddMedicalOrder = createAsyncThunk<
  OrderDto,
  { orderId: number; examId: number }
>('order/addMedicalOrder', async ({ orderId, examId }) => {
  return OrderService.addMedicalOrder(orderId, examId)
})

export const doRemoveMedicalOrder = createAsyncThunk<
  OrderDto,
  { orderId: number; medicalOrderId: number }
>('order/removeMedicalOrder', async ({ orderId, medicalOrderId }) => {
  return OrderService.removeMedicalOrder(orderId, medicalOrderId)
})

export const doUpdateOrder = createAsyncThunk<
  OrderDto,
  { id: number; dto: UpdateOrderDto }
>('order/updateOrder', async ({ id, dto }) => {
  return OrderService.update(id, dto)
})

export const doCreateChildOrder = createAsyncThunk<OrderDto, number>(
  'order/createChildOrder',
  async (orderId) => {
    return OrderService.createChildOrder(orderId)
  },
)

export const doFindChildOrders = createAsyncThunk<OrderDto[], number>(
  'order/findChildOrders',
  async (parentId) => {
    return OrderService.findChildOrders(parentId)
  },
)

export const doRemoveOrder = createAsyncThunk<void, number>(
  'order/removeOrder',
  async (id) => {
    return OrderService.removeOrder(id)
  },
)

export const doGetOrderHistory = createAsyncThunk<
  PaginatedDto<OrderDto>,
  FindOrdersQuery
>('order/getOrderHistory', async (query) => {
  return OrderService.getOrderHistory(query)
})

export const doGetOrderDocuments = createAsyncThunk<Document[], number>(
  'order/getOrderDocuments',
  async (orderId) => {
    return DocumentService.getByOrder(orderId)
  },
)

export const doCreateEmptyOrder = createAsyncThunk<OrderDto, number>(
  'order/createEmptyOrder',
  async (patientId) => {
    return OrderService.createEmptyOrder(patientId)
  },
)

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    setOrderFilters: (state, action: PayloadAction<OrderFilter>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    setSelectedOrder: (state, action: PayloadAction<OrderDto | null>) => {
      state.selectedOrder = action.payload
    },
    updateOrderDocuments: (
      state,
      action: PayloadAction<{ orderId: number; documents: Document[] }>,
    ) => {
      const { orderId, documents } = action.payload
      if (state.selectedOrder?.id === orderId) {
        state.selectedOrder.documents = documents
      }
      const order = state.orders.find((o) => o.id === orderId)
      if (order) {
        order.documents = documents
      }
    },
    setChildren: (state, action: PayloadAction<OrderDto[]>) => {
      if (state.selectedOrder) {
        state.selectedOrder.children = action.payload
      }
    },
    setOrderReport: (state, action: PayloadAction<Report>) => {
      if (state.selectedOrder) {
        for (const order of state.orders) {
          for (const medicalOrder of order.medicalOrders) {
            if (medicalOrder.id === action.payload.medicalOrderId) {
              medicalOrder.reports.push(action.payload)
              break
            }
          }
        }
      }
    },
    setSelectedMemberSituation: (
      state,
      action: PayloadAction<{
        member: MemberDto
        situation: MemberSituationDTO
      } | null>,
    ) => {
      state.selectedMemberSituation = action.payload
    },
    setFloatingMemberSituationExpanded: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isFloatingMemberSituationExpanded = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(doFindOrders.fulfilled, (state, action) => {
      state.orders = action.payload.datas
      state.filters.total = action.payload.totalCount
    })
    builder.addCase(doAddMedicalOrder.fulfilled, (state, action) => {
      if (!state.selectedOrder) {
        return
      }
      if (state.selectedOrder?.id === action.payload.id) {
        state.selectedOrder = {
          ...action.payload,
          documents: state.selectedOrder.documents,
        }
      }
      const index = state.orders.findIndex(
        (order) => order.id === action.payload.id,
      )
      if (index !== -1) {
        state.orders[index] = {
          ...action.payload,
          documents: state.orders[index].documents,
        }
      }
    })
    builder.addCase(doRemoveMedicalOrder.fulfilled, (state, action) => {
      if (!state.selectedOrder) {
        return
      }
      if (state.selectedOrder.id === action.payload.id) {
        state.selectedOrder = {
          ...action.payload,
          documents: state.selectedOrder.documents,
        }
      }
      const index = state.orders.findIndex(
        (order) => order.id === action.payload.id,
      )
      if (index !== -1) {
        state.orders[index] = {
          ...action.payload,
          documents: state.orders[index].documents,
        }
      }
    })
    builder.addCase(doUpdateOrder.fulfilled, (state, { payload }) => {
      const existingDocuments =
        state.selectedOrder?.id === payload.id
          ? state.selectedOrder.documents
          : undefined
      state.selectedOrder = { ...payload, documents: existingDocuments }
      const index = state.orders.findIndex((order) => order.id === payload.id)
      if (index !== -1) {
        state.orders[index] = {
          ...payload,
          documents: state.orders[index].documents,
        }
      }
    })
    builder.addCase(doFindChildOrders.fulfilled, (state, { payload }) => {
      if (state.selectedOrder) {
        state.selectedOrder.children = payload
      }
    })
    builder.addCase(doCreateChildOrder.fulfilled, (state, { payload }) => {
      if (state.selectedOrder) {
        state.selectedOrder.children.push(payload)
      }
    })
    builder.addCase(doRemoveOrder.fulfilled, (state, { meta }) => {
      state.orders = state.orders.filter((order) => order.id !== meta.arg)
      if (state.selectedOrder) {
        state.selectedOrder.children = state.selectedOrder.children.filter(
          (order) => order.id !== meta.arg,
        )
      }
    })
    builder.addCase(doGetOrderHistory.fulfilled, (state, { payload }) => {
      state.orderHistory = payload
    })
    builder.addCase(doCreateEmptyOrder.fulfilled, (state, { payload }) => {
      // New order — no existing documents to preserve
      state.selectedOrder = payload
    })
    builder.addCase(updatePatient.fulfilled, (state, { payload }) => {
      if (state.selectedOrder) {
        state.selectedOrder.patient = payload
      }
      state.orders.forEach((order) => {
        if (order.patient.id === payload.id) {
          order.patient = payload
        }
      })
    })
    builder.addCase(doGetOrderTransports.fulfilled, (state, action) => {
      state.orders.forEach((order) => {
        if (action.payload[order.id]) {
          order.patientTransport = action.payload[order.id]
        }
      })
      if (state.selectedOrder) {
        state.selectedOrder.patientTransport =
          action.payload[state.selectedOrder.id]
      }
    })

    builder.addCase(updatePatientTransport.fulfilled, (state, action) => {
      state.orders = state.orders.map((o) => {
        if (o.patientTransport?.id === action.payload.id) {
          return { ...o, patientTransport: action.payload }
        }
        return o
      })
      if (state.selectedOrder) {
        state.selectedOrder.patientTransport = action.payload
      }
    })
    builder.addCase(createPatientTransport.fulfilled, (state, action) => {
      if (state.selectedOrder) {
        state.selectedOrder.patientTransport = action.payload.patientTransport
      }
      state.orders.forEach((order) => {
        if (order.id === action.payload.id) {
          order.patientTransport = action.payload.patientTransport
        }
      })
    })
    builder.addCase(doCreateComment.fulfilled, (state, action) => {
      if (state.selectedOrder) {
        state.selectedOrder.commentsCount++
      }

      state.orders.forEach((order) => {
        if (order.id === action.payload.orderId) {
          order.commentsCount++
        }
      })
    })
    builder.addCase(doDeleteComment.fulfilled, (state, action) => {
      if (
        state.selectedOrder &&
        state.selectedOrder.id === action.payload.orderId
      ) {
        state.selectedOrder.commentsCount = Math.max(
          0,
          state.selectedOrder.commentsCount - 1,
        )
      }

      state.orders.forEach((order) => {
        if (order.id === action.payload.orderId) {
          order.commentsCount = Math.max(0, order.commentsCount - 1)
        }
      })
    })
    builder.addCase(doGetOrder.fulfilled, (state, action) => {
      const existingDocuments =
        state.selectedOrder?.id === action.payload.id
          ? state.selectedOrder.documents
          : undefined
      state.selectedOrder = { ...action.payload, documents: existingDocuments }
    })
    builder.addCase(doGetOrderDocuments.fulfilled, (state, action) => {
      const orderId = action.meta.arg
      if (state.selectedOrder?.id === orderId) {
        state.selectedOrder.documents = action.payload
      }
      const order = state.orders.find((o) => o.id === orderId)
      if (order) {
        order.documents = action.payload
      }
    })
    builder.addCase(
      doUpdateMedicalOrdersEquipment.fulfilled,
      (state, action) => {
        state.selectedOrder = action.payload
      },
    )
    builder.addCase(
      doUpdateMedicalOrderAttendingDoctor.fulfilled,
      (state, action) => {
        state.selectedOrder = action.payload
      },
    )
  },
})

export default orderSlice.reducer

export const {
  setOrderFilters,
  setSelectedOrder,
  setChildren,
  setOrderReport,
  setSelectedMemberSituation,
  setFloatingMemberSituationExpanded,
  updateOrderDocuments,
} = orderSlice.actions
